














































import {
  computed,
  defineComponent,
  PropType,
  ref
} from '@nuxtjs/composition-api'
import CAutocompleteInput from '~/components/shared/configurable/form/autocomplete/CAutocompleteInput.vue'
import GoogleMapsService from '~/services/GoogleMapsService'
import { useDeps } from '~/compositions/dependency-container'
import { useLogger } from '~/compositions/logger'
import PostcodeService from '~/services/location/PostcodeService'
import { PostcodeSuggestion } from '~/models/location/postcode'
import { Suggestion } from '~/components/shared/configurable/form/autocomplete/Suggestion'
import { faCompass } from '@fortawesome/free-solid-svg-icons'
import { vue3Model } from '~/utils/nuxt3-migration'
import { InputSize } from '~/models/app/input'

export default defineComponent({
  model: vue3Model,
  components: {
    CAutocompleteInput
  },
  props: {
    name: {
      type: String,
      required: false,
      default: ''
    },
    classes: {
      type: String,
      default: ''
    },
    modelValue: {
      type: Object,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    valueField: {
      type: String,
      default: null
    },
    geolocationFinderLabel: {
      type: String,
      default: null
    },
    inputId: {
      type: String,
      default: ''
    },
    inputSize: {
      type: String as PropType<InputSize>,
      default: 'md'
    },
    inputClass: {
      type: [Object, Array, String],
      default: () => {
        return []
      }
    },
    state: {
      type: Boolean,
      default: null
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    selectFirstOnClose: {
      type: Boolean,
      default: true
    },
    noAutocomplete: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const logger = useLogger()
    const [postcodeService, googleMapsService] = useDeps(
      PostcodeService,
      GoogleMapsService
    )

    const value = ref(props.modelValue)
    const postcodes = ref<PostcodeSuggestion[]>([])
    const suggestions = ref<Suggestion[]>([])
    const geolocationLoading = ref(false)

    const postcode = computed(() => {
      return value.value && value.value.name
    })

    async function findFromGeolocation() {
      if (props.disabled) {
        return
      }
      geolocationLoading.value = true
      try {
        const geolocation = await googleMapsService.getCurrentGpsGeolocation()
        const {
          postcode: closestPostcode
        } = await postcodeService.getClosestPostcode({
          lat: geolocation.lat,
          lon: geolocation.lon
        })
        value.value = {
          ...value.value,
          ...closestPostcode
        }
        emit('update:modelValue', value.value)
        emit('change', value.value)
      } catch (error) {
        if (!(error as any).response) {
          logger.captureError(error as any)
        }
      } finally {
        geolocationLoading.value = false
      }
    }
    async function handleInputChange(input: string) {
      handleInputSubmit(input)
      if (!props.noAutocomplete) {
        try {
          postcodes.value = await postcodeService.searchPostcode(input)
          suggestions.value = postcodes.value.map(getSuggestionFromPostcode)
        } catch (error) {
          if (!(error as any).response) {
            logger.captureError(error as any)
          }
          return []
        }
      }
    }
    function handleInputSubmit(input: string) {
      value.value = {
        ...value.value,
        id: null,
        name: input,
        subtext: null
      }
      emit('update:modelValue', value.value)
    }
    function handleSuggestionClear() {
      postcodes.value = []
      suggestions.value = []
    }
    function handleSuggestionSelect(id: string) {
      const selectedPostcode = postcodes.value.find(
        postcode => postcode.name === id
      )
      if (selectedPostcode) {
        value.value = selectedPostcode
        emit('update:modelValue', value.value)
        emit('change', value.value)
      }
      handleSuggestionClear()
    }
    function getSuggestionFromPostcode(
      postcode: PostcodeSuggestion
    ): Suggestion {
      return {
        id: postcode.name,
        displayText: postcode.name,
        displaySubtext: postcode.subtext
      }
    }

    return {
      faCompass,
      postcodes,
      suggestions,
      geolocationLoading,
      postcode,
      findFromGeolocation,
      handleInputChange,
      handleInputSubmit,
      handleSuggestionClear,
      handleSuggestionSelect
    }
  }
})
